import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  Row,
  Col,
  Button,
  Modal,
  Space,
  Input,
  message,
  Spin,
  Select,
  Upload,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { editImage, uploadLocalImage } from '@/services/xiaohongshu';
import FullScreenSpin from '@/components/FullscreenSpin';
import { useEditingContext } from './editingContextManager';
import RatioSelector from './ratioSelector';

interface ImageEditorProps {
  image: string; // 主要编辑的图片
  images?: string[]; // 从父组件传递的图片数组（可选）
  visible: boolean; // 控制 Modal 的显示与隐藏
  onCancel: (elapsedTime?: number) => void; // 修改：支持传递已用时间
  onConfirm?: (originalImage: string, newImage: string) => void; // 新增确认回调
  onGeneratingChange?: (isGenerating: boolean) => void; // 修改：简化生成状态回调
  onModifiedImagesChange?: (image: string, modifiedImages: string[]) => void; // 新增修改图片变化回调
  onClearParentSelection?: () => void; // 新增：清空父组件选中状态的回调
}

const { TextArea } = Input;

// 全局图片缓存管理器，避免重复请求
const imageLoadingPromises = new Map<string, Promise<void>>();
const globalImageCache = new Set<string>();

const ImageEditor: React.FC<ImageEditorProps> = ({
  image,
  images,
  visible,
  onCancel,
  onConfirm,
  onGeneratingChange,
  onModifiedImagesChange,
  onClearParentSelection,
}) => {
  const editingContextManager = useEditingContext();

  // 获取当前图片的编辑上下文
  const currentContext = editingContextManager.getContext(image);

  // 本地状态（从上下文恢复或使用默认值）
  const [suggestion, setSuggestion] = useState(
    currentContext?.suggestion || '',
  );
  const [modifiedImages, setModifiedImages] = useState<string[]>(
    currentContext?.modifiedImages || [image],
  );
  const [focus, setFocus] = useState<number>(currentContext?.focus || 0);
  const [selectedThumbnails, setSelectedThumbnails] = useState<Set<number>>(
    currentContext?.selectedThumbnails || new Set(),
  );
  const [selectedModifiedImages, setSelectedModifiedImages] = useState<
    Set<number>
  >(currentContext?.selectedModifiedImages || new Set());
  const [referencedImages, setReferencedImages] = useState<string[]>(() => {
    // 优先使用上下文中的 referencedImages (保存用户上传的图片)
    let contextImages = currentContext?.referencedImages || [];

    // 如果有传入的 images，进行智能合并
    if (images && images.length > 0) {
      // 找出传入图片中不在上下文中的新图片
      const newImages = images.filter((img) => !contextImages.includes(img));
      // 合并上下文中的图片和新传入的图片
      contextImages = [...contextImages, ...newImages];
    }

    // 如果还是没有任何图片，至少包含当前编辑的图片
    if (contextImages.length === 0) {
      contextImages = [image];
    }

    return contextImages;
  });

  // 使用contextManager的生成状态和计时器
  const regenerating = currentContext?.isGenerating || false;
  const elapsedTime = editingContextManager.getElapsedTime(image);
  const currentJobId = currentContext?.currentJobId || null;

  // 其他本地状态
  const [processing, setProcessing] = useState(false);
  const [showPhotoEditor, setShowPhotoEditor] = useState(false);
  const [selectedModel, setSelectedModel] = useState<string>('gpt-image-1');
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [imagesToGenerate, setImagesToGenerate] = useState<string[]>([]);
  const [allImages, setAllImages] = useState<string[]>(() => {
    if (images && images.length > 0) {
      return images;
    }
    return [image];
  });
  const [imageToPreview, setImageToPreview] = useState<string>(image);
  const loadingStatesRef = useRef<Map<string, boolean>>(new Map());
  const hasCleared = useRef<boolean>(false);
  const isInitialized = useRef<boolean>(false); // 标记是否已初始化
  const [dimensions, setDimensions] = useState({
    width: 1024,
    height: 1024,
    ratio: '1:1',
  });
  const [showRatioSelector, setShowRatioSelector] = useState(false);

  // 当切换到新图片时，从上下文恢复状态（只在初始化时执行）
  useEffect(() => {
    if (visible && !isInitialized.current) {
      const context = editingContextManager.getContext(image);

      if (context) {
        // 恢复所有状态 - 只在当前状态是默认值时才恢复
        if (suggestion === '') setSuggestion(context.suggestion);

        // 对于 modifiedImages，优先使用当前状态（可能包含新生成的图片）
        if (modifiedImages.length === 1 && modifiedImages[0] === image) {
          setModifiedImages(context.modifiedImages);
        }

        setFocus(context.focus);
        setSelectedThumbnails(context.selectedThumbnails);
        setSelectedModifiedImages(context.selectedModifiedImages);

        // 对于 referencedImages，智能合并传入的 images 和上下文中的 referencedImages
        let finalReferencedImages = context.referencedImages || [];

        if (images && images.length > 0) {
          // 找出传入图片中不在上下文中的新图片
          const newImages = images.filter(
            (img) => !finalReferencedImages.includes(img),
          );

          // 合并上下文中的图片和新传入的图片
          finalReferencedImages = [...finalReferencedImages, ...newImages];

          setReferencedImages(finalReferencedImages);
          // 同时更新上下文
          editingContextManager.updateContext(image, {
            referencedImages: finalReferencedImages,
          });
        } else {
          setReferencedImages(context.referencedImages);
        }

        // 恢复预览图片 - 使用focus指向的图片
        const imagesToUse =
          modifiedImages.length > 1 ? modifiedImages : context.modifiedImages;
        if (imagesToUse && imagesToUse.length > context.focus) {
          setImageToPreview(imagesToUse[context.focus]);
        }
      } else {
        // 创建新的上下文
        const initialReferencedImages =
          images && images.length > 0 ? images : [image];

        // 如果有传入的 images，使用它们作为参考图片（但不自动选中）
        if (images && images.length > 0) {
          setReferencedImages(images);
        }

        editingContextManager.createContext(image, {
          modifiedImages: [image],
          referencedImages: initialReferencedImages,
          selectedThumbnails: new Set(), // 默认不选中任何参考图片
        });
      }

      isInitialized.current = true;
    }
  }, [visible, image, editingContextManager, suggestion, modifiedImages]);

  // 监听传入的 images 变化，实时更新 referencedImages
  useEffect(() => {
    if (images && images.length > 0 && isInitialized.current) {
      // 智能合并：保留用户上传的图片，同时添加新传入的图片
      setReferencedImages((prev) => {
        // 获取当前已有的图片
        const currentImages = prev || [];

        // 找出新传入但不在当前列表中的图片
        const newImages = images.filter((img) => !currentImages.includes(img));

        // 如果没有新图片，不更新状态
        if (newImages.length === 0) {
          return prev;
        }

        // 合并现有图片和新传入的图片
        const mergedImages = [...currentImages, ...newImages];

        // 同时更新上下文
        editingContextManager.updateContext(image, {
          referencedImages: mergedImages,
        });

        return mergedImages;
      });
    }
  }, [images, editingContextManager, image]);

  // 生成成功处理 - 直接从上下文获取更新后的状态
  const handleGenerationSuccess = useCallback(
    (resp: any[]) => {
      try {
        // 从上下文获取最新状态（contextManager已经处理了图片添加）
        const updatedContext = editingContextManager.getContext(image);
        if (updatedContext) {
          // 同步上下文状态到组件状态
          setModifiedImages(updatedContext.modifiedImages);
          setFocus(updatedContext.focus);

          // 设置预览图片为第一张新生成的图片
          if (resp && resp.length > 0) {
            setImageToPreview(resp[0]);
          }

          // 通知父组件
          if (onModifiedImagesChange) {
            onModifiedImagesChange(image, updatedContext.modifiedImages);
          }
        }

        message.success('图片生成成功！');

        // 通知父组件
        if (onGeneratingChange) {
          onGeneratingChange(false);
        }
      } catch (error) {
        console.error('ImageEditor - 处理生成成功结果时出错:', error);
        message.error('处理生成结果时出错');
      }
    },
    [image, editingContextManager, onModifiedImagesChange, onGeneratingChange],
  );

  // 生成失败处理
  const handleGenerationError = useCallback(
    (error: any) => {
      console.error('生成错误:', error);

      // 更新上下文
      editingContextManager.updateContext(image, {
        isGenerating: false,
      });

      message.error('图片生成失败，请重试');

      // 通知父组件
      if (onGeneratingChange) {
        onGeneratingChange(false);
      }
    },
    [image, editingContextManager, onGeneratingChange],
  );

  // 处理生成结果 - 使用定时器轮询避免依赖引用问题
  useEffect(() => {
    if (!isInitialized.current) return;

    const checkGenerationResults = () => {
      const result = editingContextManager.getGenerationResult(image);
      if (result) {
        if (result.type === 'success') {
          const { resp } = result.data;
          if (resp && Array.isArray(resp) && resp.length > 0) {
            handleGenerationSuccess(resp);
          }
        } else if (result.type === 'error') {
          handleGenerationError(result.data);
        }

        // 清除结果避免重复处理
        editingContextManager.clearGenerationResult(image);
      }
    };

    // 立即检查一次
    checkGenerationResults();

    // 定期检查结果
    const interval = setInterval(checkGenerationResults, 1000);

    return () => clearInterval(interval);
  }, [
    image,
    editingContextManager,
    handleGenerationSuccess,
    handleGenerationError,
  ]);

  // 开始重新生成
  const handleRegenerate = useCallback(async () => {
    if (!isInitialized.current) {
      return;
    }

    // 检查是否已经有任务在进行
    if (currentJobId) {
      message.warning('已有生成任务在进行中，请等待完成');
      return;
    }

    try {
      const currentPrompt = suggestion;

      // 使用选中的图片或当前图片
      const imagesToUse =
        imagesToGenerate.length > 0
          ? imagesToGenerate
          : [modifiedImages[focus]];

      const response = await editImage({
        suggestion: currentPrompt,
        images: imagesToUse,
        model: selectedModel,
        dimension: { width: dimensions.width, height: dimensions.height },
      });

      if (response && (response as any).resp && (response as any).resp.jobId) {
        const jobId = (response as any).resp.jobId;

        // 使用contextManager开始生成任务
        editingContextManager.startGeneration(image, jobId);

        // 通知父组件
        if (onGeneratingChange) {
          onGeneratingChange(true);
        }
      } else {
        throw new Error('未获取到有效的jobId');
      }
    } catch (error) {
      console.error('生成失败:', error);
      message.error('生成失败，请重试');
    }
  }, [
    currentJobId,
    suggestion,
    imagesToGenerate,
    modifiedImages,
    focus,
    selectedModel,
    image,
    editingContextManager,
    onGeneratingChange,
  ]);

  // 格式化经过时间显示
  const formatElapsedTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Modal关闭处理
  const handleCancel = useCallback(() => {
    // 保存当前状态到上下文
    editingContextManager.updateContext(image, {
      suggestion,
      modifiedImages,
      focus,
      selectedThumbnails,
      selectedModifiedImages,
      referencedImages,
    });

    // 重置初始化标记，下次打开时重新初始化
    isInitialized.current = false;
    hasCleared.current = false;

    // 传递已用时间给父组件
    onCancel(elapsedTime);
  }, [
    image,
    suggestion,
    modifiedImages,
    focus,
    selectedThumbnails,
    selectedModifiedImages,
    referencedImages,
    elapsedTime,
    editingContextManager,
    onCancel,
  ]);

  // 处理图片上传
  // 处理图片上传
  const handleImageUpload = useCallback(
    async (file: any) => {
      if (!isInitialized.current) return false; // 确保组件已初始化

      console.log('=== 开始上传图片 (imageEditor_new) ===');
      console.log('文件信息:', {
        name: file.name,
        size: file.size,
        type: file.type,
      });

      try {
        message.loading({ content: '正在上传图片...', key: 'uploading' });

        // 上传到 OSS
        const response: any = await uploadLocalImage({ file });

        console.log('上传响应:', response);

        // 检查组件是否仍然挂载
        if (!isInitialized.current) return false;

        if (response['resp']['code'] === 0) {
          const ossUrl = response.resp.url;
          console.log('上传成功，OSS URL:', ossUrl);

          // 添加到referencedImages数组
          if (isInitialized.current) {
            console.log('当前 referencedImages:', referencedImages);

            setReferencedImages((prev) => {
              const newImages = [...prev, ossUrl];
              console.log('更新后的 referencedImages:', newImages);

              // 同时更新上下文
              editingContextManager.updateContext(image, {
                referencedImages: newImages,
              });
              console.log('已更新上下文');

              return newImages;
            });

            message.success({
              content: '图片上传成功!',
              key: 'uploading',
              duration: 3,
            });
          }
        } else {
          console.error('上传失败，响应码:', response['resp']['code']);
          if (isInitialized.current) {
            message.error({
              content: '图片上传失败，请重试',
              key: 'uploading',
            });
          }
        }
      } catch (error) {
        console.error('图片上传失败:', error);
        if (isInitialized.current) {
          message.error({
            content: '图片上传失败，请重试',
            key: 'uploading',
          });
        }
      }

      return false; // 阻止默认上传行为
    },
    [editingContextManager, image, referencedImages],
  );

  // 处理缩略图选中/取消选中
  const handleThumbnailToggle = (index: number, event: React.MouseEvent) => {
    event.stopPropagation(); // 防止触发预览切换

    setSelectedThumbnails((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index); // 取消选中
      } else {
        newSet.add(index); // 选中
      }
      return newSet;
    });
  };

  // 处理修改图片选中/取消选中
  const handleModifiedImageToggle = (
    index: number,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation(); // 防止触发focus切换

    setSelectedModifiedImages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index); // 取消选中
      } else {
        newSet.add(index); // 选中
      }
      return newSet;
    });
  };

  // 渲染比例图标
  const renderRatioIcon = (ratio: string) => {
    // 解析比例字符串为数值
    const getRatioValue = (ratioStr: string) => {
      if (ratioStr === 'custom') return 1;
      const [w, h] = ratioStr.split(':').map(Number);
      return w / h;
    };

    const ratioValue = getRatioValue(ratio);
    const containerSize = 16;
    let rectWidth, rectHeight;

    if (ratioValue >= 1) {
      // 横向矩形
      rectWidth = containerSize;
      rectHeight = containerSize / ratioValue;
    } else {
      // 纵向矩形
      rectHeight = containerSize;
      rectWidth = containerSize * ratioValue;
    }

    return (
      <div
        style={{
          width: `${containerSize}px`,
          height: `${containerSize}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            width: `${rectWidth}px`,
            height: `${rectHeight}px`,
            border: '1px solid white',
            borderRadius: '2px',
            backgroundColor: 'transparent',
          }}
        />
      </div>
    );
  };

  // 处理输出图片尺寸选择
  const handleDimensionChange = (
    width: number,
    height: number,
    ratio: string,
  ) => {
    setDimensions({ width, height, ratio });
    console.log('尺寸变更:', { width, height, ratio });
  };

  // 处理缩略图点击预览
  const handleThumbnailPreview = (index: number) => {
    if (
      referencedImages &&
      referencedImages.length > 0 &&
      referencedImages[index]
    ) {
      setImageToPreview(referencedImages[index]);
      setSelectedImageIndex(index);
    }
  };

  // 处理修改图片点击预览
  const handleModifiedImagePreview = (index: number) => {
    if (modifiedImages && modifiedImages.length > 0 && modifiedImages[index]) {
      setImageToPreview(modifiedImages[index]);
      setFocus(index);
    }
  };

  // 更新imagesToGenerate数组
  useEffect(() => {
    const allSelectedImages: string[] = [];

    // 添加选中的缩略图
    if (referencedImages && referencedImages.length > 0) {
      const selectedImages = Array.from(selectedThumbnails).map(
        (index) => referencedImages[index],
      );
      allSelectedImages.push(...selectedImages);
    }

    // 添加选中的修改图片
    const selectedModified = Array.from(selectedModifiedImages).map(
      (index) => modifiedImages[index],
    );
    allSelectedImages.push(...selectedModified);

    // 如果没有选中任何图片，使用当前focus的图片
    if (allSelectedImages.length === 0) {
      allSelectedImages.push(modifiedImages[focus]);
    }

    // 去重
    const uniqueImages = Array.from(new Set(allSelectedImages));
    setImagesToGenerate(uniqueImages);
  }, [
    selectedThumbnails,
    selectedModifiedImages,
    modifiedImages,
    focus,
    referencedImages,
  ]);

  // 图片预加载函数
  const preloadImage = useCallback((src: string): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
      img.src = src;
    });
  }, []);

  // 优化的图片组件
  const CachedImage = useMemo(() => {
    return React.memo<{
      src: string;
      alt: string;
      style?: React.CSSProperties;
      onClick?: () => void;
    }>(({ src, alt, style, onClick }) => {
      const [loaded, setLoaded] = useState(false);
      const [error, setError] = useState(false);

      useEffect(() => {
        preloadImage(src)
          .then(() => setLoaded(true))
          .catch(() => setError(true));
      }, [src]);

      if (error) {
        return (
          <div
            style={{
              ...style,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#f5f5f5',
              color: '#999',
            }}
          >
            加载失败
          </div>
        );
      }

      if (!loaded) {
        return (
          <div
            style={{
              ...style,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#f5f5f5',
            }}
          >
            <Spin size="small" />
          </div>
        );
      }

      return <img src={src} alt={alt} style={style} onClick={onClick} />;
    });
  }, [preloadImage]);

  return (
    <>
      {processing && <FullScreenSpin />}
      {showPhotoEditor && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.65)', // 半透明黑色遮罩
          }}
        >
          {/* 固定尺寸容器，避免 Flex 重新布局导致尺寸被压成 0 */}
          <div
            style={{
              width: '80vw',
              height: '80vh',
              background: '#1e1e1e',
              borderRadius: 8,
              overflow: 'hidden',
            }}
          ></div>
        </div>
      )}
      <Modal
        title={
          referencedImages && referencedImages.length >= 2
            ? `图片编辑 (${referencedImages.length}张参考图片)`
            : '图片编辑'
        }
        open={visible && !showPhotoEditor}
        footer={null}
        onCancel={handleCancel}
        closable={true} // 始终允许关闭按钮
        maskClosable={true} // 始终允许点击遮罩关闭
        width={1200} // 增加宽度以容纳新的布局
        style={{ top: 20 }} // 调整顶部距离
      >
        <Spin spinning={regenerating} tip="正在生成图片...">
          {/* 显示已流逝时间 - 只在生成时显示 */}
          {regenerating && (
            <div
              style={{
                position: 'absolute',
                left: '20px',
                bottom: '20px',
                background: 'rgba(0, 0, 0, 0.8)',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '500',
                zIndex: 1001,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              }}
            >
              <span
                style={{
                  width: '8px',
                  height: '8px',
                  background: '#1890ff',
                  borderRadius: '50%',
                }}
              ></span>
              已用时 {`${elapsedTime} s`}
            </div>
          )}
          <Row gutter={16}>
            {/* 左边展示图片 */}
            <Col span={12}>
              {/* 始终显示缩略图区域和预览区域的上下分割布局 */}
              <div
                style={{
                  height: '80vh', // 增加总高度
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* 上方：缩略图区域 - 始始终显示 */}
                <div
                  style={{
                    height: '98px',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '8px', // 改为统一的8px padding，更紧凑
                    marginBottom: '12px', // 减少底部间距，为预览区域让出更多空间
                    border: '1px solid #e9ecef',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      height: '100%',
                      overflowX: 'auto',
                      overflowY: 'hidden',
                      paddingBottom: '0px', // 移除底部额外padding
                    }}
                  >
                    {/* 上传按钮 */}
                    <Upload
                      showUploadList={false}
                      beforeUpload={(file) => {
                        const isJpgOrPng =
                          file.type === 'image/jpeg' ||
                          file.type === 'image/png' ||
                          file.type === 'image/webp';
                        if (!isJpgOrPng) {
                          message.error('只能上传 JPG/PNG/WEBP 格式的图片!');
                          return false;
                        }
                        const isLt25M = file.size / 1024 / 1024 < 25;
                        if (!isLt25M) {
                          message.error('图片大小不能超过 25MB!');
                          return false;
                        }
                        if (isJpgOrPng && isLt25M) {
                          handleImageUpload(file);
                        }
                        return false; // 阻止默认上传
                      }}
                      disabled={regenerating}
                    >
                      <div
                        style={{
                          width: '80px',
                          height: '80px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: regenerating ? 'not-allowed' : 'pointer',
                          border: '2px dashed #d9d9d9',
                          borderRadius: '6px',
                          backgroundColor: regenerating ? '#f5f5f5' : '#fafafa',
                          transition: 'all 0.2s ease',
                          opacity: regenerating ? 0.5 : 1,
                        }}
                        onMouseEnter={(e) => {
                          if (!regenerating) {
                            e.currentTarget.style.borderColor = '#1890ff';
                            e.currentTarget.style.backgroundColor = '#f0f8ff';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!regenerating) {
                            e.currentTarget.style.borderColor = '#d9d9d9';
                            e.currentTarget.style.backgroundColor = '#fafafa';
                          }
                        }}
                      >
                        <PlusOutlined
                          style={{
                            fontSize: '16px',
                            color: regenerating ? '#ccc' : '#999',
                            marginBottom: '4px',
                          }}
                        />
                        <div
                          style={{
                            fontSize: '12px',
                            color: regenerating ? '#ccc' : '#666',
                          }}
                        >
                          上传
                        </div>
                      </div>
                    </Upload>
                    {/* 显示参考图片缩略图 - 如果有的话 */}
                    {console.log(
                      '渲染缩略图，当前 referencedImages:',
                      referencedImages,
                    )}
                    {referencedImages &&
                      referencedImages.length > 0 &&
                      referencedImages.map((img, idx) => (
                        <div
                          key={idx}
                          style={{
                            position: 'relative',
                            flexShrink: 0,
                            width: '80px',
                            height: '80px',
                            cursor: 'pointer',
                            transition: 'transform 0.2s ease',
                          }}
                          onClick={() => handleThumbnailPreview(idx)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          <CachedImage
                            src={img}
                            alt={`缩略图 ${idx + 1}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              borderRadius: '6px',
                              border:
                                imageToPreview === img
                                  ? '3px solid #52c41a' // 预览边框用绿色
                                  : '2px solid #dee2e6',
                              transition: 'all 0.2s ease',
                              boxShadow:
                                imageToPreview === img
                                  ? '0 4px 12px rgba(82, 196, 26, 0.3)' // 预览阴影
                                  : '0 2px 4px rgba(0,0,0,0.1)',
                            }}
                          />

                          {/* 图片序号 */}
                          <div
                            style={{
                              position: 'absolute',
                              top: '-4px',
                              right: '-4px',
                              background:
                                imageToPreview === img
                                  ? '#52c41a' // 预览状态用绿色
                                  : '#6c757d',
                              color: 'white',
                              borderRadius: '50%',
                              width: '20px',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '10px',
                              fontWeight: 'bold',
                              border: '2px solid white',
                              transition: 'all 0.2s ease',
                            }}
                          >
                            {idx + 1}
                          </div>

                          {/* 选中复选框 */}
                          <div
                            style={{
                              position: 'absolute',
                              top: '4px',
                              left: '4px',
                              width: '18px',
                              height: '18px',
                              borderRadius: '3px',
                              backgroundColor: selectedThumbnails.has(idx)
                                ? '#1890ff'
                                : 'rgba(255, 255, 255, 0.8)',
                              border: selectedThumbnails.has(idx)
                                ? '2px solid #1890ff'
                                : '2px solid rgba(0, 0, 0, 0.3)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              zIndex: 10,
                            }}
                            onClick={(e) => handleThumbnailToggle(idx, e)}
                          >
                            {selectedThumbnails.has(idx) && (
                              <span
                                style={{
                                  color: 'white',
                                  fontSize: '10px',
                                  fontWeight: 'bold',
                                }}
                              >
                                ✓
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                {/* 下方：大图预览区域 */}
                <div
                  style={{
                    flex: 1,
                    background: 'transparent', // 改为透明背景
                    borderRadius: '8px',
                    overflow: 'hidden',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #e9ecef', // 添加边框以便于识别区域
                  }}
                >
                  {/* 统一预览图片 */}
                  {imageToPreview ? (
                    <CachedImage
                      src={imageToPreview}
                      alt="预览图片"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        borderRadius: '4px',
                        transition: 'all 0.3s ease',
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#999',
                        fontSize: '16px',
                      }}
                    >
                      <Spin size="large" />
                    </div>
                  )}
                </div>
              </div>
            </Col>
            {/* 右边选择区域 */}
            <Col span={12}>
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <div
                  style={{
                    padding: '20px',
                    background: '#f5f5f5',
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <h3 style={{ margin: 0 }}>修改图片</h3>
                  <Select
                    value={selectedModel}
                    onChange={setSelectedModel}
                    style={{ width: 150 }}
                    options={[
                      { value: 'gpt-image-1', label: 'gpt-image-1' },
                      { value: 'jimeng-3.0', label: 'jimeng-3.0' },
                      { value: 'nano-banana', label: 'nano-banana' },
                    ]}
                  />
                </div>
                <div
                  style={{
                    width: '100%',
                    overflowX: 'auto',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Space>
                    {console.log(
                      '渲染修改图片列表，当前 modifiedImages:',
                      modifiedImages,
                    )}
                    {modifiedImages.map((img, idx) => (
                      <div
                        key={`modified-${idx}-${img}`}
                        style={{
                          position: 'relative',
                          display: 'inline-block',
                        }}
                      >
                        <img
                          src={img}
                          alt={`编辑图片 ${idx}`}
                          style={{
                            width: 96,
                            height: 96,
                            borderRadius: '8px',
                            objectFit: 'cover',
                            border:
                              imageToPreview === img
                                ? '2px solid #2ba245'
                                : 'none',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            if (!regenerating) {
                              handleModifiedImagePreview(idx);
                            }
                          }}
                        />
                        {/* 选中复选框 */}
                        <div
                          style={{
                            position: 'absolute',
                            top: '4px',
                            left: '4px',
                            width: '18px',
                            height: '18px',
                            borderRadius: '3px',
                            backgroundColor: selectedModifiedImages.has(idx)
                              ? '#1890ff'
                              : 'rgba(255, 255, 255, 0.8)',
                            border: selectedModifiedImages.has(idx)
                              ? '2px solid #1890ff'
                              : '2px solid rgba(0, 0, 0, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            zIndex: 10,
                          }}
                          onClick={(e) => handleModifiedImageToggle(idx, e)}
                        >
                          {selectedModifiedImages.has(idx) && (
                            <span
                              style={{
                                color: 'white',
                                fontSize: '10px',
                                fontWeight: 'bold',
                              }}
                            >
                              ✓
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                    {/* 显示正在生成的占位符 */}
                    {regenerating && (
                      <div
                        style={{
                          width: 96,
                          height: 96,
                          borderRadius: '8px',
                          border: '2px solid #1890ff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#f0f0f0',
                          flexDirection: 'column',
                        }}
                      >
                        <Spin size="small" />
                        <div
                          style={{
                            fontSize: '12px',
                            marginTop: '8px',
                            color: '#666',
                          }}
                        >
                          正在生成
                        </div>
                      </div>
                    )}
                  </Space>
                </div>
                {/* 修改 TextArea 区域，添加相对定位容器 */}
                <div style={{ position: 'relative', width: '100%' }}>
                  <TextArea
                    autoSize={{ minRows: 6 }}
                    placeholder="输入对图片更改的建议"
                    value={suggestion}
                    onChange={(e) => setSuggestion(e.target.value)}
                    disabled={regenerating}
                    style={{
                      paddingBottom: '40px', // 为底部按钮留出空间
                    }}
                  />

                  {/* 尺寸比例按钮 - 输入框左下角 */}
                  <div style={{ position: 'relative' }}>
                    <Button
                      size="small"
                      onClick={() => setShowRatioSelector(!showRatioSelector)}
                      style={{
                        position: 'absolute',
                        bottom: '8px',
                        left: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        backgroundColor: showRatioSelector
                          ? '#1890ff'
                          : 'rgba(0, 0, 0, 0.7)',
                        borderColor: showRatioSelector
                          ? '#1890ff'
                          : 'rgba(255, 255, 255, 0.3)',
                        color: 'white',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '12px',
                        height: 'auto',
                        minHeight: '28px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                        zIndex: 10,
                      }}
                    >
                      {/* 动态渲染比例图标 */}
                      {renderRatioIcon(dimensions.ratio)}
                      <span>
                        {dimensions.ratio === 'custom'
                          ? '自定义'
                          : dimensions.ratio}
                      </span>
                    </Button>

                    {/* 悬浮的尺寸选择器 - 显示在按钮上方 */}
                    {showRatioSelector && (
                      <div
                        style={{
                          position: 'absolute',
                          bottom: '40px', // 在按钮上方
                          left: '8px',
                          zIndex: 1000,
                          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
                          border: '1px solid #404040',
                          minWidth: '400px',
                          animation: 'fadeInUp 0.2s ease-out',
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <RatioSelector
                          defaultWidth={dimensions.width}
                          defaultHeight={dimensions.height}
                          defaultRatio={dimensions.ratio}
                          selectedModel={selectedModel} // 新增：传递模型类型
                          onChange={handleDimensionChange}
                          onConfirm={(width, height, ratio) => {
                            setDimensions({ width, height, ratio });
                            console.log('确认尺寸设置:', {
                              width,
                              height,
                              ratio,
                            });
                            setShowRatioSelector(false);
                          }}
                          onCancel={() => setShowRatioSelector(false)}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  type="primary"
                  onClick={handleRegenerate}
                  style={{ width: '100%' }}
                  loading={regenerating}
                  disabled={regenerating}
                >
                  {regenerating ? '正在生成...' : '智能生成'}
                </Button>
                <Row gutter={16}>
                  <Col span={12}>
                    <Button
                      type="primary"
                      onClick={() => {
                        setShowPhotoEditor(true);
                      }}
                      style={{ width: '100%' }}
                      disabled={regenerating}
                    >
                      手动调整
                    </Button>
                  </Col>
                  <Col span={12}>
                    <Button
                      type="primary"
                      onClick={() => {
                        // 确认修改：用当前focus的图片替换原始图片
                        const currentImage = modifiedImages[focus];
                        if (onConfirm && currentImage) {
                          onConfirm(image, currentImage);
                        }
                        handleCancel(); // 关闭模态框
                      }}
                      style={{
                        width: '100%',
                        backgroundColor: '#2ba245',
                        borderColor: '#2ba245',
                      }}
                      disabled={regenerating}
                    >
                      确认修改
                    </Button>
                  </Col>
                </Row>
              </Space>
            </Col>
          </Row>
        </Spin>
      </Modal>

      {/* 尺寸选择弹窗 */}
      {showRatioSelector && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
            borderRadius: '8px',
            background: 'transparent',
          }}
          onClick={() => setShowRatioSelector(false)}
        />
      )}
    </>
  );
};

export default ImageEditor;
